import { getApiClient } from '@/lib/api/api-client';
import type { Immersive } from '@/types';
import { logger } from '@/lib/utils/logger';
import { getCacheDuration } from '@/lib/config/cache.config';

const DEBUG = process.env.NODE_ENV === 'development';
import type {
  BaseQueryParams,
  PaginatedResponse,
  CatalogItem,
  FindInstallationsParams,
  WhereFilter,
} from './catalog-types';
import { getSortValue } from './catalog-types';
import { CacheService } from '../cache';
import { immersiveTransformer } from '@/lib/transformers/immersives.transformer';
import { handleApiError, success, type ApiResult } from '@/lib/utils/errorHandler';

export function parseSearchParams(params: URLSearchParams): Partial<BaseQueryParams> {
  return {
    page: params.get('page') ? parseInt(params.get('page')!) : undefined,
    limit: params.get('limit') ? parseInt(params.get('limit')!) : 10,
    query: params.get('query') || undefined,
    depth: params.get('depth') ? parseInt(params.get('depth')!) : undefined,
    sort: params.get('sort') || undefined,
  };
}

/**
 * Transform Immersive to CatalogItem
 */
export function immersiveToCatalogItem(immersive: Immersive): CatalogItem {
  return immersiveTransformer.toCatalogItem(immersive);
}

/**
 * API service for Immersive experiences
 */
export class ImmersiveAPI {
  static apiClient = getApiClient();
  private static CACHE_DURATION = getCacheDuration('immersives');

  /**
   * Get immersive experience list
   */
  static async findImmersives(
    params: FindInstallationsParams & BaseQueryParams
  ): Promise<ApiResult<PaginatedResponse<CatalogItem>>> {
    const { page = 1, limit = 10, query, sort, years, ...rest } = params;

    const sortValue = getSortValue(sort, { dateField: 'createdAt' });

    // Build where query for Payload CMS v3
    const where: WhereFilter = {};

    // Search query
    if (query) {
      where.title = {
        contains: query,
      };
    }

    // Year filter - use date field for Immersive
    if (years && years.length > 0) {
      try {
        // Build OR query for multiple years
        const yearConditions = years.map(year => {
          const startDate = new Date(Number(year), 0, 1).toISOString();
          const endDate = new Date(Number(year), 11, 31, 23, 59, 59).toISOString();

          return {
            or: [
              // For date field
              {
                and: [{ date: { greater_than_equal: startDate } }, { date: { less_than_equal: endDate } }],
              },
              // Fallback to createdAt if date is null
              {
                and: [
                  { date: { exists: false } },
                  { createdAt: { greater_than_equal: startDate } },
                  { createdAt: { less_than_equal: endDate } },
                ],
              },
            ],
          };
        });

        // Combine year filters with OR
        if (yearConditions.length === 1) {
          // Single year selected, add directly
          Object.assign(where, yearConditions[0]);
        } else {
          // Multiple years, combine with OR
          where.or = yearConditions;
        }
      } catch (error) {
        // Error handling is centralized, continue with other filters
      }
    }

    // Additional where parameters
    if (rest.where) {
      Object.assign(where, rest.where);
    }

    if (DEBUG) {
      logger.warn('Immersive API where query:', where);
    }

    try {
      const response = await ImmersiveAPI.apiClient.collections.immersives.find({
        page,
        limit,
        where,
        depth: params.depth || 2,
        // TODO: type properly - getSortValue returns dynamic strings not in payload-rest-client's keyof DOC union
        sort: sortValue as never,
      });

      // Cast API response to Immersive type
      const result = response as PaginatedResponse<Immersive>;

      // Calculate totalPages and check pagination
      if (result.totalDocs && result.limit) {
        const totalPages = Math.ceil(result.totalDocs / result.limit);
        result.totalPages = totalPages;

        // Check if there are more pages
        result.hasNextPage = result.page < totalPages;

        // Transform results to CatalogItem
        const catalogItems = result.docs.map(immersiveToCatalogItem);

        if (DEBUG) {
          logger.warn(
            `API response calculated: Page ${result.page}/${totalPages}, hasNextPage: ${result.hasNextPage}, totalDocs: ${result.totalDocs}, limit: ${result.limit}`
          );
        }

        // Return result as new object to avoid type mismatch
        return success({
          ...result,
          docs: catalogItems,
        } as PaginatedResponse<CatalogItem>);
      } else {
        // Default to false if totalDocs or limit is missing
        result.hasNextPage = false;
        if (DEBUG) {
          logger.warn(`API response calculated: hasNextPage: false (missing totalDocs or limit)`);
        }

        // Return empty docs array as CatalogItem array
        return success({
          ...result,
          docs: [],
        } as PaginatedResponse<CatalogItem>);
      }
    } catch (error) {
      return handleApiError('findImmersives', error);
    }
  }

  /**
   * Get immersive experience details by slug
   */
  static async findImmersiveBySlug(slug: string, skipCache: boolean = false): Promise<ApiResult<Immersive>> {
    const cacheKey = await CacheService.scopedKey(`exhibition.service_${slug}`);
    // Check cache (if skipCache is false)
    if (!skipCache) {
      const cachedData = CacheService.getFromCache(cacheKey);
      if (cachedData) {
        return success(cachedData as Immersive);
      }
    }

    try {
      const response = await ImmersiveAPI.apiClient.collections.immersives.find({
        where: {
          slug: {
            equals: slug,
          },
        },
        depth: 2,
        limit: 1,
      });

      if (response.docs.length === 0) {
        // Not found is not an error, just return null
        return success(null as unknown as Immersive);
      }

      // Write to cache
      CacheService.addToCache(cacheKey, response.docs[0], this.CACHE_DURATION);

      return success(response.docs[0]);
    } catch (error) {
      return handleApiError('findImmersiveBySlug', error);
    }
  }
}
